import fetcher from '@utils/api-helper/fetch';
import { HOST_TYPE } from '@utils/api-helper/host';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useDispatch, useStore as useReduxStore } from 'react-redux';
import { fetchStaticQuestions } from '@actions/quizTaking';
import pushRoute from '@utils/pushRoute';
import { TEST_STARTED } from '@constants/segment';
import { useSnackbar } from '@stores/snackbar';
import quizTakingTypes from '../../../redux/constants/quizTaking';
import useDSTSegmentAnalytics from './Common/useDSTSegmentAnalytics';
import { TEST_DETAILS_PAGE } from './constants';

const useDSTActions = () => {
  const [loading, setLoading] = useState(false);
  const store = useReduxStore();
  const router = useRouter();
  const { goalUID, goalSlug } = router.query;
  const dispatch = useDispatch();

  const segmentAnalytics = useDSTSegmentAnalytics();
  const { open: showSnackbar } = useSnackbar();

  const sendAnalyticsOnEnroll = (languageDisplayName, isReattempt) => {
    segmentAnalytics(TEST_STARTED, {
      is_reattempt: isReattempt,
      language: languageDisplayName,
      last_primary_source: TEST_DETAILS_PAGE
    });
  };

  const setClassPreference = async (classUID) => {
    setLoading(true);
    const response = await fetcher(
      `scholarship/v1/${goalUID}/class_preference`,
      {
        dispatch: store.dispatch,
        getState: store.getState,
        requestType: 'POST',
        useSearchToken: true,
        hostType: HOST_TYPE.TEST_SERIES,
        body: {
          class_uid: classUID
        }
      }
    );
    setLoading(false);
    return response;
  };

  const enrollInQuiz = async (quizUID, selectedLanguageCode) => {
    const res = await dispatch({
      type: quizTakingTypes.QUIZ_ENROLL,
      endpoint: `v3/quizzes/quiz/${quizUID}/enroll/`,
      method: 'post',
      data: { language: selectedLanguageCode },
      quizUID,
      useSearchToken: true,
      hostType: HOST_TYPE.TEST_SERIES
    });
    return res;
  };

  const handleTestStart = async ({ languageInfo, classUID, isReattempt }) => {
    setLoading(true);
    try {
      const response = await fetcher('v1/quizzes/scholarship-test/create/', {
        dispatch: store.dispatch,
        getState: store.getState,
        requestType: 'POST',
        body: {
          language_code: languageInfo.code,
          class_preference_uid: classUID,
          goal_uid: goalUID
        }
      });
      if (response?.quiz_uid) {
        const quizUID = response.quiz_uid;
        enrollInQuiz(quizUID, languageInfo.code).then(async (res) => {
          if (res.error) {
            return showSnackbar({
              message: 'Unable to enroll in test, please try again',
              type: 'error'
            });
          }
          sendAnalyticsOnEnroll(languageInfo?.displayName, isReattempt);
          dispatch(fetchStaticQuestions(quizUID)).then(() => {
            pushRoute({
              pathname: '/goal/[goalSlug]/[goalUID]/scholarship-test/[quizUID]',
              query: {
                goalSlug,
                goalUID,
                quizUID
              }
            });
          });
        });
      }
      return response;
    } catch (error) {
      setLoading(false);
      showSnackbar({
        message: error?.message || 'Maximum quizzes attempted for this goal',
        type: 'error'
      });
    }
  };

  return {
    setClassPreference,
    handleTestStart,
    loading
  };
};

export default useDSTActions;
